import i18n, { type TFunction } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { compile } from '~/routes/paths'
import { exists, sanitizeUnsafeContent } from '~/utils'

import {
  activeCustomRoles,
  canonicalOutputOrder,
  type CustomLabels,
  type QuestionAssignments,
  resolveOutputTypeLabel,
} from '../atoms'

import { type CommentsMap } from './useGenerateAnalysisComments'
import { type BasicOutputsResult } from './useGenerateBasicOutputs'
import {
  type SavedImageEntry,
  type SavedImagesMap,
} from './useRenderAndSaveDocumentImages'

const createDocumentMutationDocument = graphql(`
  mutation BasicOutputGenerationPage_CreateDocument(
    $brandAccountId: ID!
    $content: String!
    $title: String!
  ) {
    createDocument(
      brandAccountId: $brandAccountId
      content: $content
      title: $title
    ) {
      id
    }
  }
`)

/**
 * Escapes the four HTML-significant characters so the value can be safely
 * embedded in attribute values or text nodes.
 *
 * @param str - Raw string to escape.
 * @returns Escaped string safe for HTML interpolation.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Builds the HTML body for a single basic-output report document
 * (either an A-doc covering brand-common content or a B-doc scoped to one
 * brand). Composes header, customer/per-brand/customized sections in the
 * canonical output order and inlines comments where available.
 *
 * @param filterName - Name of the filter pattern this document represents.
 * @param surveyTitle - Survey title to show in the document header.
 * @param brandGroupName - Brand-group (category) name interpolated into the
 *   report heading for an A-doc; ignored for a B-doc, whose heading instead
 *   names the brand scoped via `targetBrandId`.
 * @param savedImages - All saved-image entries for the filter (pre-scoped).
 * @param comments - Analysis comments keyed by the unique outputKey. May be undefined if comments were not generated.
 * @param customLabels - Custom-role label text used to resolve output labels.
 * @param questionAssignments - Question assignment map; its custom keys (in
 *   insertion order) drive custom-section ordering.
 * @param docKind - 'A' for brand-common doc, 'B' for per-brand doc.
 * @param targetBrandId - Brand id used to scope a B-doc; required when
 *   `docKind === 'B'`, otherwise ignored.
 * @param t - Translation function for the report's user-visible labels.
 * @returns HTML string for the document body.
 */
export function buildDocumentHtml(
  filterName: string,
  surveyTitle: string,
  brandGroupName: string,
  savedImages: SavedImageEntry[],
  comments: Map<string, string> | undefined,
  customLabels: CustomLabels,
  questionAssignments: QuestionAssignments,
  docKind: 'A' | 'B',
  targetBrandId: number | null,
  t: TFunction,
): string {
  let scoped: SavedImageEntry[]
  if (docKind === 'A') {
    // Brand-common entries are identified by the structural sentinel brandId === null
    // (see useRenderAndSaveDocumentImages), not the brandName string, so a brand
    // literally named "all" is not misclassified into the A-doc.
    scoped = savedImages.filter((e) => e.brandId === null)
  } else {
    if (targetBrandId === null) {
      throw new Error('B-doc requires targetBrandId')
    }
    scoped = savedImages.filter((e) => e.brandId === targetBrandId)
  }
  if (scoped.length === 0) {
    throw new Error(
      `${docKind}-doc has zero entries post-filter — render pipeline bug (filter='${filterName}', targetBrandId=${targetBrandId})`,
    )
  }

  const date = new Date().toLocaleDateString(i18n.language)
  // B-doc heading names the brand itself (scoped[0] is safe post the
  // zero-entries check above); A-doc keeps naming the brand-group/category.
  const headingText =
    docKind === 'A'
      ? t('basic_output_report_heading', { category: brandGroupName })
      : t('basic_output_report_heading_brand', { brand: scoped[0].brandName })
  let html = `<h1>${escapeHtml(headingText)}</h1>`
  html += `<p>${escapeHtml(
    t('basic_output_report_meta', {
      date,
      filter: filterName,
      survey: surveyTitle,
    }),
  )}</p>`

  // Separate into: customer data (9segs), per-brand, customized analysis.
  // Brand-common entries are identified by the structural sentinel
  // brandId === null (see useRenderAndSaveDocumentImages, which also asserts
  // brandName === 'all' iff brandId === null), not the brandName string.
  const customerImages = scoped.filter(
    (img) =>
      img.brandId === null && !img.outputType.startsWith('customizedAnalysis'),
  )
  // Exclude customized-analysis images from the per-brand bucket: they are
  // emitted in their own `customizedImages` section, so a (defensive) non-null
  // brandId must not also place them here and double-render.
  const perBrandImages = scoped.filter(
    (img) =>
      img.brandId !== null && !img.outputType.startsWith('customizedAnalysis'),
  )
  const customizedImages = scoped.filter((img) =>
    img.outputType.startsWith('customizedAnalysis'),
  )

  const renderImageSection = (
    img: SavedImageEntry,
    headingTag: 'h2' | 'h3',
  ) => {
    const label = resolveOutputTypeLabel(img.outputType, customLabels, t)
    // 基礎データ集のカスタマイズ分析は 9segs 固定なので、方式を併記しても情報が増えない
    const hideSegmentSuffix =
      img.outputType === 'customizedAnalysis_npiDriver' ||
      img.outputType === 'customizedAnalysis_importance'
    const segLabel =
      !hideSegmentSuffix &&
      (img.segmentMode === '5segs' || img.segmentMode === '9segs')
        ? ` (${img.segmentMode})`
        : ''
    // A-doc can hold one copy of the same group-wide table per 自社 brand, identical
    // except for its comment, so the heading must name the framing brand or the copies
    // are indistinguishable. Appended inline rather than as its own heading level: an
    // A-doc legitimately holds several brands' worth of these copies side by side,
    // unlike a B-doc, which is already scoped and titled to a single brand.
    const framingLabel = exists(img.framingBrandName)
      ? `｜${t('basic_output_own_brand_perspective', {
          name: img.framingBrandName,
        })}`
      : ''
    html += `<${headingTag}>${escapeHtml(
      `${label}${segLabel}${framingLabel}`,
    )}</${headingTag}>`

    // Direct lookup by the unique outputKey carried on each saved image.
    const findComment = (): string | undefined => comments?.get(img.outputKey)
    const rawComment = findComment()
    if (rawComment) {
      const escapedComment = escapeHtml(rawComment).replace(/\n/g, '<br />')
      html += `<div class="analysis-comment"><p>${escapedComment}</p></div>`
    }

    html += `<img src="${escapeHtml(img.imageURL)}" alt="${escapeHtml(img.title)}" class="basic-output-image" />`
    const deepLink = `${window.location.origin}${compile('restore', {
      key: img.restoreKey,
    })}`
    html += `<p><a href="${escapeHtml(deepLink)}">${escapeHtml(img.title)}</a></p>`
    html += `<hr />`
  }

  // Custom questions sort after canonical types, in insertion order (the order
  // their keys were added to questionAssignments) — consistent with display and
  // planRenderTasks.
  const customRoleOrder = activeCustomRoles(questionAssignments)
  const orderOf = (outputType: string): number => {
    const canonicalIdx = canonicalOutputOrder.indexOf(outputType)
    if (canonicalIdx !== -1) {
      return canonicalIdx
    }
    // Custom standard questions carry a `standardAnalysis_<key>` outputType; strip the prefix to match the custom key.
    const customKey = outputType.startsWith('standardAnalysis_')
      ? outputType.slice('standardAnalysis_'.length)
      : outputType
    const customIdx = customRoleOrder.findIndex((r) => r === customKey)
    if (customIdx !== -1) {
      return canonicalOutputOrder.length + customIdx
    }

    return canonicalOutputOrder.length + customRoleOrder.length
  }
  const sortByCanonicalOrder = (a: SavedImageEntry, b: SavedImageEntry) => {
    const aOrder = orderOf(a.outputType)
    const bOrder = orderOf(b.outputType)
    if (aOrder !== bOrder) {
      return aOrder - bOrder
    }
    if (a.segmentMode === b.segmentMode) {
      return 0
    }

    return a.segmentMode === '5segs' ? -1 : 1
  }

  // 1. Customer data (9segs distribution)
  if (0 < customerImages.length) {
    for (const img of [...customerImages].sort(sortByCanonicalOrder)) {
      renderImageSection(img, 'h2')
    }
  }

  // 2. Per-brand sections
  if (0 < perBrandImages.length) {
    const brandMap = new Map<string, SavedImageEntry[]>()
    for (const img of perBrandImages) {
      const existing = brandMap.get(img.brandName) ?? []
      existing.push(img)
      brandMap.set(img.brandName, existing)
    }

    const sortedBrands = [...brandMap.keys()].sort()

    // This loop only ever runs for B-docs (A-doc's `scoped` already excludes
    // non-null brandId above), and the B-doc's own <h1> now names that same
    // brand, so a repeated `<h2>{brandName}</h2>` here would be a redundant
    // subheading rather than new information.
    for (const brandName of sortedBrands) {
      const brandImages = brandMap.get(brandName) ?? []

      for (const img of [...brandImages].sort(sortByCanonicalOrder)) {
        renderImageSection(img, 'h3')
      }
    }
  }

  // 3. Customized analysis
  if (0 < customizedImages.length) {
    for (const img of [...customizedImages].sort(sortByCanonicalOrder)) {
      renderImageSection(img, 'h2')
    }
  }

  // ここで一括で通すことで、A/B 両方の呼び出し元で個別にサニタイズし忘れる
  // 心配がなくなる。href は常にこの関数内で組み立てた復元リンクのみで
  // AIコメント由来の値は含まないため、実際に効くのは style 属性の除去だけ。
  return sanitizeUnsafeContent(html)
}

type DocumentProgress = {
  completed: number
  errors: Error[]
  isComplete: boolean
  total: number
}

const initialProgress: DocumentProgress = {
  completed: 0,
  errors: [],
  isComplete: false,
  total: 0,
}

type StartContext = {
  brandAccountId: string
  brandGroupName: string
  commentsMap: CommentsMap
  customLabels: CustomLabels
  questionAssignments: QuestionAssignments
  result: BasicOutputsResult
  savedImagesMap: SavedImagesMap
  surveyTitle: string
}

export const useGenerateDocuments = () => {
  const graphQLRequest = useGraphQLRequest()
  const { t } = useTranslation()
  const [progress, setProgress] =
    React.useState<DocumentProgress>(initialProgress)

  const start = React.useCallback(
    async (context: StartContext) => {
      const {
        brandAccountId,
        brandGroupName,
        commentsMap,
        customLabels,
        questionAssignments,
        result,
        savedImagesMap,
        surveyTitle,
      } = context

      const filterResults = result.filterResults

      // Collect all unique brand IDs across all filters from savedImagesMap
      const brandMap = new Map<number, string>()
      for (const entries of savedImagesMap.values()) {
        for (const entry of entries) {
          if (entry.brandId !== null) {
            brandMap.set(entry.brandId, entry.brandName)
          }
        }
      }
      const brands = Array.from(brandMap.entries())
        .map(([brandId, brandName]) => ({ brandId, brandName }))
        .sort((a, b) => a.brandId - b.brandId)

      setProgress({
        completed: 0,
        errors: [],
        isComplete: false,
        total: filterResults.length * (1 + brands.length),
      })

      const errors: Error[] = []
      let successes = 0
      const date = new Date().toLocaleDateString(i18n.language)

      for (let i = 0; i < filterResults.length; i++) {
        const filterResult = filterResults[i]
        const savedImages = savedImagesMap.get(String(i)) ?? []

        // A-doc (brand-common) — skipped when the run has no brand-common
        // entries (Step 2 did not target ブランドグループ全体), mirroring the
        // B-doc hasEntries skip below.
        const hasCommonEntries = savedImages.some((e) => e.brandId === null)
        if (hasCommonEntries) {
          const titleA = t('basic_output_report_title', {
            brand: brandGroupName,
            date,
            filter: filterResult.filterName,
            survey: surveyTitle,
          })
          try {
            const htmlA = buildDocumentHtml(
              filterResult.filterName,
              surveyTitle,
              brandGroupName,
              savedImages,
              commentsMap,
              customLabels,
              questionAssignments,
              'A',
              null,
              t,
            )
            const created = await graphQLRequest({
              document: createDocumentMutationDocument,
              variables: { brandAccountId, content: htmlA, title: titleA },
            })
            if (!exists(created.createDocument?.id)) {
              throw new Error(t('creation_failed'))
            }
            successes++
          } catch (error) {
            errors.push(
              error instanceof Error ? error : new Error(String(error)),
            )
          }
          setProgress((prev) => ({
            ...prev,
            completed: prev.completed + 1,
            errors: [...errors],
          }))
        } else {
          setProgress((prev) => ({
            ...prev,
            errors: [...errors],
            total: prev.total - 1,
          }))
        }

        // B-docs (one per brand, ascending brand id)
        for (const brand of brands) {
          const hasEntries = savedImages.some(
            (e) => e.brandId === brand.brandId,
          )
          if (!hasEntries) {
            setProgress((prev) => ({
              ...prev,
              errors: [...errors],
              total: prev.total - 1,
            }))
            continue
          }
          const titleB = t('basic_output_report_title', {
            brand: brand.brandName,
            date,
            filter: filterResult.filterName,
            survey: surveyTitle,
          })
          try {
            const htmlB = buildDocumentHtml(
              filterResult.filterName,
              surveyTitle,
              brandGroupName,
              savedImages,
              commentsMap,
              customLabels,
              questionAssignments,
              'B',
              brand.brandId,
              t,
            )
            const created = await graphQLRequest({
              document: createDocumentMutationDocument,
              variables: { brandAccountId, content: htmlB, title: titleB },
            })
            if (!exists(created.createDocument?.id)) {
              throw new Error(t('creation_failed'))
            }
            successes++
          } catch (error) {
            errors.push(
              error instanceof Error ? error : new Error(String(error)),
            )
          }
          setProgress((prev) => ({
            ...prev,
            completed: prev.completed + 1,
            errors: [...errors],
          }))
        }
      }

      setProgress((prev) => ({ ...prev, isComplete: true }))

      // Total failure: every attempted document errored. Resolving here would let
      // the flow flip to 'complete' and show 「完了しました」 for a run that produced
      // nothing. Throw so the caller's .catch surfaces it as an error state.
      // Partial success (successes > 0) still resolves → 'complete'.
      if (successes === 0 && 0 < errors.length) {
        throw errors[0]
      }
    },
    [graphQLRequest, t],
  )

  return { progress, start }
}
